/***************************************************************************************************
** favoritedPages
***************************************************************************************************/

import { handleStaleAppVersion, Router } from "@utils";

const IGNORED_QUERY_KEYS = [
  "page",
  "page_size",
];

const canonicalizeQueryString = (queryString) => {
  if (!queryString) return "";

  const searchParams = new URLSearchParams(queryString);

  IGNORED_QUERY_KEYS.forEach((key) => searchParams.delete(key));

  const entries = [...searchParams.entries()];
  if (entries.length === 0) return "";

  entries.sort((left, right) => {
    if (left[0] !== right[0]) return left[0].localeCompare(right[0]);
    return left[1].localeCompare(right[1]);
  });

  const canonicalParams = new URLSearchParams();
  entries.forEach(([key, value]) => canonicalParams.append(key, value));

  return canonicalParams.toString();
};

export const normalizeFavoritedPagePath = (pathname) => {
  if (!pathname) return pathname;

  const withoutHash = pathname.split("#")[0];

  const queryIndex = withoutHash.indexOf("?");
  const pathPart = queryIndex === -1 ? withoutHash : withoutHash.slice(0, queryIndex);
  const queryString = queryIndex === -1 ? "" : withoutHash.slice(queryIndex + 1);

  const segments = pathPart.split("/").filter((segment) => segment.length > 0);

  let normalizedPath;

  if (segments.length === 0) {
    normalizedPath = "/";
  } else if (segments[0] === "q") {
    normalizedPath = segments.length >= 2 ? `/${segments[0]}/${segments[1]}` : pathPart;
  } else if (segments.length >= 3) {
    normalizedPath = `/${segments[0]}/${segments[1]}/${segments[2]}`;
  } else {
    normalizedPath = pathPart;
  }

  const canonicalQuery = canonicalizeQueryString(queryString);

  return canonicalQuery ? `${normalizedPath}?${canonicalQuery}` : normalizedPath;
};

export const findFavoritedPageByPath = (path) => {
  const favoritedPages = window.FAVORITED_PAGES || [];
  const normalizedPath = normalizeFavoritedPagePath(path);

  return favoritedPages.find((favoritedPage) => favoritedPage.path === normalizedPath);
};

export const deleteFavoritedPage = async (id) => {
  const url = Router.buildUrlFor(`/api/favorited-kinn-pages/${id}`);

  const response = await fetch(url, {
    method: "DELETE",
    headers: {
      "Accept": "application/json",
      "Cache-Control": "no-cache, no-store",
      "Authorization-Token": window.appContext.authorizationToken,
    },
  });

  const responseBody = await response.json().catch(() => ({ error: response.statusText }));

  handleStaleAppVersion(response);

  if (!response.ok) {
    throw new Error(responseBody.error || responseBody.status);
  }

  return responseBody;
};

export const buildFavoritedPageActionMenuItem = ({
  path,
  pageKey,
  defaultLabel,
  defaultIcon,
  resourceName,
}) => {
  if (!path) return null;

  const normalizedPath = normalizeFavoritedPagePath(path.startsWith("/") ? path : `/${path}`);

  return {
    type: "favorited_page_toggle",
    path: normalizedPath,
    pageKey: pageKey,
    defaultLabel: defaultLabel,
    defaultIcon: resourceName ? `resource://${resourceName}` : defaultIcon?.name,
  };
};

export const buildFavoritedPagesMenuItems = ({ favoritedPages }) => {
  return favoritedPages
    .filter((favoritedPage) => favoritedPage.enabled)
    .map((favoritedPage) => ({
      type: "link",
      label: {
        text: favoritedPage.label_text,
        icon: favoritedPage.label_icon,
      },
      path: favoritedPage.path,
    }));
};
